import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MENU } from './chatwootMenuConfig';
import './chatwootWidget.css';

const API = '/widget'; // rotas no Fastify backend — sem token exposto no browser
const SESSION_CONTACT = 'cw_widget_contact';
const SESSION_CONV    = 'cw_widget_conv';
const SESSION_SETOR   = 'cw_widget_setor';

const STEPS = {
  WELCOME: 'welcome',
  SETOR:   'setor',
  INFO:    'info',    // tela informativa (ex: Vagas)
  NOME:    'nome',
  LOADING: 'loading',
  CHAT:    'chat',
};

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export default function ChatwootMenuWidget({ label = 'Atendimento On-Line', className = '' }) {
  const [open, setOpen]         = useState(false);
  const [step, setStep]         = useState(STEPS.WELCOME);
  const [setor, setSetor]       = useState(null);
  const [nome, setNome]         = useState('');
  const [empresa, setEmpresa]   = useState('');
  const [error, setError]       = useState('');
  const [contactId, setContactId] = useState(null);
  const [convId, setConvId]       = useState(null);
  const [messages, setMessages]   = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending]     = useState(false);
  const [agentOnline, setAgentOnline] = useState(false);
  const pollRef  = useRef(null);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  // ── Restaurar sessão ─────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const savedContact = sessionStorage.getItem(SESSION_CONTACT);
      const savedConv    = sessionStorage.getItem(SESSION_CONV);
      const savedSetor   = JSON.parse(sessionStorage.getItem(SESSION_SETOR) || 'null');
      if (savedContact && savedConv) {
        setContactId(savedContact);
        setConvId(Number(savedConv));
        if (savedSetor) setSetor(savedSetor);
        setStep(STEPS.CHAT);
      }
    } catch { /* sessão corrompida */ }
  }, []);

  // ── Scroll ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (open && step === STEPS.CHAT) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    }
  }, [messages, open, step]);

  useEffect(() => {
    if (open && step === STEPS.CHAT) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [open, step]);

  // ── Polling de mensagens ─────────────────────────────────────────────────────
  const fetchMessages = useCallback(async (_cId, cConvId) => {
    try {
      const res = await fetch(`${API}/messages?convId=${cConvId}`);
      if (!res.ok) return;
      const data = await res.json();
      const msgs = (data.payload || [])
        .filter(m => m.message_type !== 2 && m.content)
        .sort((a, b) => a.created_at - b.created_at);
      setMessages(msgs);
      setAgentOnline(msgs.some(m => m.message_type === 1));
    } catch { /* ignora falha de polling */ }
  }, []);

  useEffect(() => {
    if (step !== STEPS.CHAT || !contactId || !convId) return;
    fetchMessages(contactId, convId);
    pollRef.current = setInterval(() => fetchMessages(contactId, convId), 5000);
    return () => clearInterval(pollRef.current);
  }, [step, contactId, convId, fetchMessages]);

  // ── Criar contato + conversa ─────────────────────────────────────────────────
  async function handleIniciar() {
    if (!nome.trim())    { setError('Por favor, informe seu nome.'); return; }
    if (!empresa.trim()) { setError('Por favor, informe o nome da empresa.'); return; }
    setError('');
    setStep(STEPS.LOADING);

    const nomeCompleto = `${nome.trim()} - ${empresa.trim()}`;
    const sessionId    = `w_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;

    try {
      // Fastify cria contato, conversa, aplica label e atribui equipe em uma chamada
      const res = await fetch(`${API}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: nome.trim(), empresa: empresa.trim(), label: setor.id }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `status ${res.status}`);
      }
      const { convId: conversationId, contactIdentifier } = await res.json();

      sessionStorage.setItem(SESSION_CONTACT, contactIdentifier);
      sessionStorage.setItem(SESSION_CONV,    String(conversationId));
      sessionStorage.setItem(SESSION_SETOR,   JSON.stringify(setor));

      setContactId(contactIdentifier);
      setConvId(conversationId);
      setStep(STEPS.CHAT);
    } catch (e) {
      console.error('[ChatwootMenuWidget]', e);
      setError('Erro ao iniciar a conversa. Tente novamente.');
      setStep(STEPS.NOME);
    }
  }

  // ── Enviar mensagem ──────────────────────────────────────────────────────────
  async function handleSend() {
    const text = inputText.trim();
    if (!text || sending) return;
    setSending(true);
    setInputText('');
    try {
      await fetch(`${API}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ convId, content: text }),
      });
      await fetchMessages(null, convId);
    } catch { /* ignora */ } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }

  // ── Nova conversa ────────────────────────────────────────────────────────────
  function handleNovaConversa() {
    sessionStorage.removeItem(SESSION_CONTACT);
    sessionStorage.removeItem(SESSION_CONV);
    sessionStorage.removeItem(SESSION_SETOR);
    setContactId(null); setConvId(null);
    setMessages([]); setSetor(null);
    setNome(''); setEmpresa('');
    setStep(STEPS.WELCOME);
  }

  // ── Seleção do setor ─────────────────────────────────────────────────────────
  function handleSetor(item) {
    setSetor(item);
    if (item.info) {
      setStep(STEPS.INFO);
    } else {
      setStep(STEPS.NOME);
    }
  }

  // ── Telas ────────────────────────────────────────────────────────────────────

  function renderWelcome() {
    return (
      <div className="cwm-welcome">
        <div className="cwm-avatar-lg"><i className="bi bi-headset" /></div>
        <h3 className="cwm-welcome-title">Olá! Como podemos ajudar?</h3>
        <p className="cwm-welcome-sub">Selecione o assunto e conectamos você ao setor certo.</p>
        <button className="cwm-btn-primary" onClick={() => setStep(STEPS.SETOR)}>
          Iniciar atendimento <i className="bi bi-arrow-right ms-2" />
        </button>
      </div>
    );
  }

  function renderSetor() {
    return (
      <>
        <div className="cwm-screen-header">
          <span>Qual o motivo do contato?</span>
        </div>
        <div className="cwm-menu-list">
          {MENU.map(item => (
            <button key={item.id} className="cwm-menu-card" onClick={() => handleSetor(item)}>
              <i className={`bi ${item.icon} cwm-card-icon`} />
              <span className="cwm-card-label">{item.label}</span>
              <i className={`bi ${item.info ? 'bi-info-circle' : 'bi-chevron-right'} cwm-card-arrow`} />
            </button>
          ))}
        </div>
      </>
    );
  }

  function renderInfo() {
    const info = setor?.info;
    return (
      <div className="cwm-welcome">
        <div className="cwm-avatar-lg" style={{ background: '#eff6ff', color: '#2563eb' }}>
          <i className="bi bi-briefcase" />
        </div>
        <h3 className="cwm-welcome-title">{setor?.label}</h3>
        <p className="cwm-welcome-sub">{info?.message}</p>
        <a
          href={info?.url}
          target="_blank"
          rel="noreferrer"
          className="cwm-btn-primary"
          style={{ textDecoration: 'none', marginBottom: 12 }}
        >
          <i className="bi bi-box-arrow-up-right me-2" />
          {info?.urlLabel}
        </a>
        <button
          className="cwm-btn-secondary"
          onClick={() => setStep(STEPS.SETOR)}
        >
          <i className="bi bi-arrow-left me-2" />
          Voltar ao menu
        </button>
      </div>
    );
  }

  function renderNome() {
    return (
      <>
        <div className="cwm-screen-header">
          <button className="cwm-back" onClick={() => setStep(STEPS.SETOR)}>
            <i className="bi bi-arrow-left" />
          </button>
          <span>Quase lá!</span>
        </div>
        <div className="cwm-nome-form">
          <div className="cwm-breadcrumb">
            <i className={`bi ${setor?.icon} me-2`} style={{ color: '#28a745' }} />
            {setor?.label}
          </div>
          <label className="cwm-label">Seu nome</label>
          <input
            className="cwm-input" type="text"
            placeholder="Ex: João Silva" maxLength={60}
            value={nome} onChange={e => setNome(e.target.value)} autoFocus
          />
          <label className="cwm-label">Empresa</label>
          <input
            className="cwm-input" type="text"
            placeholder="Ex: Empresa ABC" maxLength={80}
            value={empresa} onChange={e => setEmpresa(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleIniciar()}
          />
          {error && <div className="cwm-error">{error}</div>}
          <button className="cwm-btn-primary" onClick={handleIniciar}>
            <i className="bi bi-chat-dots me-2" />
            Iniciar conversa
          </button>
        </div>
      </>
    );
  }

  function renderLoading() {
    return (
      <div className="cwm-welcome">
        <div className="cwm-spinner" />
        <p className="cwm-welcome-sub">Conectando...</p>
      </div>
    );
  }

  function renderChat() {
    return (
      <>
        <div className="cwm-chat-subheader">
          <i className={`bi ${setor?.icon || 'bi-chat'} me-1`} style={{ color: '#28a745' }} />
          <span>{setor?.label}</span>
          <button className="cwm-nova-btn" onClick={handleNovaConversa} title="Nova conversa">
            <i className="bi bi-plus-circle" /> Nova conversa
          </button>
        </div>
        <div className="cwm-messages">
          {messages.length === 0 ? (
            <div className="cwm-msg-empty">
              <i className="bi bi-hourglass-split" style={{ fontSize: '1.3rem', display: 'block', marginBottom: 8 }} />
              Aguardando um agente...
            </div>
          ) : (
            messages.map(msg => {
              const isAgent = msg.message_type === 1;
              return (
                <div key={msg.id} className={`cwm-msg ${isAgent ? 'agent' : 'client'}`}>
                  {isAgent && <div className="cwm-msg-sender">{msg.sender?.name || 'Agente'}</div>}
                  <div className="cwm-bubble">{msg.content}</div>
                  <div className="cwm-msg-time">{formatTime(msg.created_at)}</div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>
        <div className="cwm-reply">
          <textarea
            ref={inputRef}
            className="cwm-reply-input"
            placeholder="Digite sua mensagem..."
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            rows={2}
            disabled={sending}
          />
          <button className="cwm-reply-send" onClick={handleSend}
            disabled={!inputText.trim() || sending} title="Enviar">
            {sending ? <i className="bi bi-hourglass-split" /> : <i className="bi bi-send-fill" />}
          </button>
        </div>
      </>
    );
  }

  // ── Render principal ─────────────────────────────────────────────────────────
  const hasSession = step === STEPS.CHAT;

  return (
    <>
      <button
        className={`cw-public-btn ${className}`}
        onClick={() => setOpen(o => !o)}
        title="Falar com nosso atendimento"
      >
        <span className="cw-online-indicator online" />
        <i className="bi bi-headset" />
        <span>{hasSession ? 'Continuar atendimento' : label}</span>
      </button>

      {open && (
        <>
          <div className="cwm-overlay" onClick={() => setOpen(false)} />
          <div className="cwm-panel">
            <div className="cwm-header">
              <div className="cwm-header-avatar"><i className="bi bi-headset" /></div>
              <div className="cwm-header-info">
                <div className="cwm-header-title">Suporte Transtec</div>
                <div className="cwm-header-status">
                  <span className="cwm-online-dot" />
                  {agentOnline ? 'Agente online' : 'Aguardando agente'}
                </div>
              </div>
              <button className="cwm-close-btn" onClick={() => setOpen(false)}>
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className="cwm-body">
              {step === STEPS.WELCOME && renderWelcome()}
              {step === STEPS.SETOR   && renderSetor()}
              {step === STEPS.INFO    && renderInfo()}
              {step === STEPS.NOME    && renderNome()}
              {step === STEPS.LOADING && renderLoading()}
              {step === STEPS.CHAT    && renderChat()}
            </div>

            <div className="cwm-footer-brand">
              Atendimento seguro · Transtec World
            </div>
          </div>
        </>
      )}

      <style>{`
        .cw-public-btn {
          display: inline-flex; align-items: center; gap: 8px; width: 100%;
          padding: 10px 16px; font-weight: 700; font-size: 0.92rem;
          border: none; border-radius: 8px;
          background: linear-gradient(135deg, #1a6b3a 0%, #28a745 100%);
          color: #fff; cursor: pointer; justify-content: center;
          transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s;
          box-shadow: 0 3px 10px rgba(40,167,69,0.35);
        }
        .cw-public-btn:hover { opacity: 0.92; transform: translateY(-1px); box-shadow: 0 5px 16px rgba(40,167,69,0.45); }
        .cw-online-indicator {
          width: 9px; height: 9px; border-radius: 50%; background: #90EE90; flex-shrink: 0;
          animation: cw-pub-pulse 2s ease infinite;
        }
        @keyframes cw-pub-pulse {
          0%,100% { box-shadow: 0 0 0 0 rgba(144,238,144,0.6); }
          50%      { box-shadow: 0 0 0 5px rgba(144,238,144,0); }
        }
      `}</style>
    </>
  );
}
